/**
 * Memory management utilities for weak devices
 */

class MemoryManager {
  private static instance: MemoryManager;
  private cleanupTasks: (() => void)[] = [];
  private memoryWarningThreshold = 0.8; // 80% of available memory

  static getInstance(): MemoryManager {
    if (!MemoryManager.instance) {
      MemoryManager.instance = new MemoryManager();
    }
    return MemoryManager.instance;
  }

  /**
   * Register a cleanup task to be called during memory pressure
   */
  registerCleanupTask(task: () => void): void {
    this.cleanupTasks.push(task);
  }

  /**
   * Remove a cleanup task
   */
  unregisterCleanupTask(task: () => void): void {
    const index = this.cleanupTasks.indexOf(task);
    if (index > -1) {
      this.cleanupTasks.splice(index, 1);
    }
  }

  /**
   * Force cleanup of all registered tasks
   */
  performCleanup(): void {
    console.log('🧹 Performing memory cleanup, tasks:', this.cleanupTasks.length);
    this.cleanupTasks.forEach(task => {
      try {
        task();
      } catch (error) {
        console.warn('Cleanup task failed:', error);
      }
    });
  }

  /**
   * Initialize memory monitoring
   */
  initializeMemoryMonitoring(): void {
    // Check for memory API support
    if ('memory' in performance) {
      // Monitor memory usage periodically
      setInterval(() => {
        const memInfo = (performance as any).memory;
        const usageRatio = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
        
        if (usageRatio > this.memoryWarningThreshold) {
          console.warn('⚠️ High memory usage detected:', Math.round(usageRatio * 100) + '%');
          this.performCleanup();
        }
      }, 30000); // Check every 30 seconds
    }

    // Listen for page visibility changes to cleanup when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.performCleanup();
      }
    });

    // Cleanup before page unload
    window.addEventListener('beforeunload', () => {
      this.performCleanup();
    });
  }

  /**
   * Check if device is likely low-end based on memory and connection
   */
  isLowEndDevice(): boolean {
    // Check memory (if available)
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      if (memInfo.jsHeapSizeLimit < 1073741824) { // Less than 1GB
        return true;
      }
    }

    // Check connection speed
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
        return true;
      }
    }

    return false;
  }
}

export { MemoryManager };